import type Phaser from "phaser";
import "@fontsource/pixelify-sans/400.css";
import "@fontsource/pixelify-sans/700.css";
import "./ui/ui.css";
import { Sfx } from "./audio/Sfx.js";
import { createGame } from "./game/createGame.js";
import { net } from "./net/NetClient.js";
import { radio } from "./radio/radio.js";
import { isUiFocused, setGame, setGameKeyboardEnabled } from "./runtime.js";
import { store } from "./store/GameStore.js";
import { ChatPanel } from "./ui/ChatPanel.js";
import { el } from "./ui/dom.js";
import { ExitDialog } from "./ui/ExitDialog.js";
import { Hud } from "./ui/Hud.js";
import { LeaderboardPanel } from "./ui/LeaderboardPanel.js";
import { MobileNotice } from "./ui/MobileNotice.js";
import { RadioPanel } from "./ui/RadioPanel.js";
import { ResultsModal } from "./ui/ResultsModal.js";
import { TitleScreen } from "./ui/TitleScreen.js";
import { Toasts } from "./ui/Toasts.js";

const uiRoot = document.getElementById("ui-root")!;
const gameRoot = document.getElementById("game-root")!;

new MobileNotice(uiRoot);
const toasts = new Toasts(uiRoot);
const sfx = new Sfx();

let leaderboard: LeaderboardPanel;
const hud = new Hud(uiRoot, () => leaderboard.toggle());
const chat = new ChatPanel(uiRoot);
leaderboard = new LeaderboardPanel(uiRoot);
const results = new ResultsModal(uiRoot);
const exitDialog = new ExitDialog(uiRoot);
const radioPanel = new RadioPanel(uiRoot);

hud.mount();
chat.mount();
leaderboard.mount();
results.mount();
exitDialog.mount();
radioPanel.mount();
radio.mount();
toasts.mount();

const reconnectOverlay = el("div", "reconnect-overlay hidden", "Reconnecting…");
uiRoot.appendChild(reconnectOverlay);

let game: Phaser.Game | undefined;

const title = new TitleScreen(uiRoot, async (input) => {
  // Still inside the Join gesture: unlock audio so the radio can follow the server later.
  radio.player.unlock();
  try {
    await net.join(input);
  } catch (error) {
    console.error("[agency] join failed", error);
    title.showError(friendlyJoinError(error));
  }
});

store.on("connection", (connection) => {
  if (connection.state === "joined") startGame();
  else if (connection.state === "reconnecting") reconnectOverlay.classList.remove("hidden");
  else if (connection.state === "closed") {
    reconnectOverlay.classList.add("hidden");
    endGame();
  }
});

store.on("chat", () => sfx.chat());
store.on("playerEvent", (event) => {
  if (event.sessionId === store.sessionId && event.event === "burnout") sfx.burnout();
});
store.on("results", () => sfx.fanfare());

window.addEventListener("keydown", (event) => {
  if (isUiFocused()) return;
  if (event.key === "Enter") {
    event.preventDefault();
    chat.focusInput();
  } else if (event.key === "Tab") {
    event.preventDefault();
    leaderboard.toggle();
  } else if (event.key === "Escape") {
    exitDialog.hide();
    radioPanel.hide();
    hud.radioChip.close();
    results.hide();
    leaderboard.hide();
  } else if (event.key.toLowerCase() === "m") {
    toasts.push(sfx.toggleMute() ? "Sound muted" : "Sound on");
  }
});

title.show();

function startGame(): void {
  reconnectOverlay.classList.add("hidden");
  title.hide();
  hud.show();
  chat.show();
  if (!game) {
    game = createGame(gameRoot);
    setGame(game);
  } else {
    // A chat input still focused from before a reconnect would otherwise leak
    // typing into the game once keys are re-enabled.
    if (isUiFocused()) (document.activeElement as HTMLElement).blur();
    setGameKeyboardEnabled(true);
    game.scene.start("Office");
  }
}

function endGame(): void {
  // Hide chat first: its blur re-enables the keyboard of the still-running scene.
  chat.hide();
  if (game) game.scene.stop("Office");
  hud.hide();
  leaderboard.hide();
  results.hide();
  exitDialog.hide();
  radioPanel.hide();
  // Phaser's key captures are global; release them so the title inputs get W/A/S/D, space, etc.
  setGameKeyboardEnabled(false);
  title.show();
  toasts.push("See you tomorrow 👋");
}

function friendlyJoinError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/full/i.test(message)) return "The office is full — try again in a bit.";
  if (/nickname/i.test(message)) return "That nickname isn't allowed.";
  if (/role/i.test(message)) return "That role isn't available yet.";
  if (/version/i.test(message)) return "Please refresh to update the game.";
  if (/profile/i.test(message)) return "Couldn't load your profile — try again in a moment.";
  return "Couldn't join the office. Is the server running?";
}
